import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MeResponse, MyLearningRow } from "../api";
import { getCertificateDownload, getMyLearning } from "../api";
import { Card, Shell } from "../ui";

const STATUS_LABEL: Record<string, string> = {
  NOT_STARTED: "Not started",
  IN_PROGRESS: "In progress",
  ASSESSMENT_DUE: "Assessment due",
  REMEDIATION_REQUIRED: "Remediation required",
  COMPLETED: "Completed",
};

const SUMMARY_TILES: Array<{ label: string; status: MyLearningRow["status"] | "ALL" }> = [
  { label: "Assigned", status: "ALL" },
  { label: "In progress", status: "IN_PROGRESS" },
  { label: "Assessment due", status: "ASSESSMENT_DUE" },
  { label: "Failed / needs retry", status: "REMEDIATION_REQUIRED" },
  { label: "Completed", status: "COMPLETED" },
];

export function LearnerHome({ me }: { me: MeResponse }) {
  const [rows, setRows] = useState<MyLearningRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    getMyLearning()
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  async function downloadCertificate(trackId: string) {
    setDownloadingId(trackId);
    setDownloadError(null);
    try {
      const { url } = await getCertificateDownload(trackId);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloadingId(null);
    }
  }

  const certificates = (rows ?? []).filter((r) => r.credential?.state === "ISSUED");

  return (
    <Shell
      title="My Learning"
      subtitle={`Signed in as ${me.displayName}`}
      nav={<a href="/logout">Sign out</a>}
    >
      {error && <Card>Could not load your learning: {error}</Card>}

      {rows && rows.length > 0 && (
        <Card>
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            {SUMMARY_TILES.map((tile) => (
              <div key={tile.label}>
                <div style={{ fontSize: "1.4rem", fontWeight: 600 }}>
                  {tile.status === "ALL"
                    ? rows.length
                    : rows.filter((r) => r.status === tile.status).length}
                </div>
                <div style={{ color: "#666", fontSize: "0.85rem" }}>{tile.label}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <h2 style={{ marginTop: "2rem", marginBottom: "0.5rem", fontSize: "1.1rem" }}>
        Courses assigned
      </h2>
      {rows?.length === 0 && <Card>Nothing has been assigned to you yet.</Card>}
      {rows?.map((r) => (
        <Card key={r.enrollmentId}>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {r.coverImageUrl ? (
              <img
                src={r.coverImageUrl}
                alt=""
                style={{
                  width: 96,
                  height: 64,
                  objectFit: "cover",
                  borderRadius: 8,
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: 96,
                  height: 64,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: "linear-gradient(135deg, #1a1a2e, #e5aa5a)",
                }}
              />
            )}
            <div style={{ flex: 1, minWidth: 200 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "1rem",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <Link
                    to={`/learn/tracks/${r.trackId}`}
                    style={{ fontWeight: 600, fontSize: "1.05rem" }}
                  >
                    {r.programmeTitle}
                  </Link>
                  <div style={{ color: "#666", fontSize: "0.9rem" }}>{r.trackTitle}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div>{STATUS_LABEL[r.status] ?? r.status}</div>
                  <div style={{ color: "#666", fontSize: "0.9rem" }}>
                    {r.progressPercent}% complete
                  </div>
                </div>
              </div>
              <div
                style={{
                  marginTop: "0.6rem",
                  height: 6,
                  borderRadius: 999,
                  background: "#eee",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${r.progressPercent}%`,
                    height: "100%",
                    borderRadius: 999,
                    background: r.progressPercent >= 100 ? "#0a7d33" : "#e5aa5a",
                  }}
                />
              </div>
            </div>
          </div>
        </Card>
      ))}

      <h2 style={{ marginTop: "2rem", marginBottom: "0.5rem", fontSize: "1.1rem" }}>
        My certificates
      </h2>
      {downloadError && <Card>Could not prepare the download: {downloadError}</Card>}
      {rows && certificates.length === 0 && (
        <Card>No certificates yet — they'll appear here once you complete a programme.</Card>
      )}
      {certificates.map((r) => (
        <Card key={r.enrollmentId}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            <div>
              <strong>{r.programmeTitle}</strong>
              <div style={{ color: "#666", fontSize: "0.9rem" }}>
                {r.trackTitle}
                {r.credential?.credentialId && ` · Credential ID: ${r.credential.credentialId}`}
              </div>
            </div>
            <button
              onClick={() => downloadCertificate(r.trackId)}
              disabled={downloadingId === r.trackId}
              style={{
                padding: "0.5rem 1rem",
                background: "#1a1a2e",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: downloadingId === r.trackId ? "default" : "pointer",
                fontSize: "0.9rem",
              }}
            >
              {downloadingId === r.trackId ? "Preparing…" : "Download certificate"}
            </button>
          </div>
        </Card>
      ))}
    </Shell>
  );
}
